/**
 * Quiz player — one question at a time, graded server-side on submit.
 *
 * Rendered by the training page after the module's content when a quiz is
 * attached and not yet passed. The result screen shows the score against
 * the pass threshold with a per-question review (which were missed, never
 * the right answers); pass hands off to ``onPassed`` (module completion),
 * fail offers a retake (a fresh append-only attempt server-side).
 */

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2Icon, XCircleIcon } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getModuleQuiz,
  submitQuizAttempt,
  type AttemptResult,
} from "@/services/training-quizzes";
import { triggerQuizPassed } from "./training-celebration";

interface TrainingQuizPlayerProps {
  moduleId: string;
  /** Fires after a passing attempt when the learner clicks Finish. */
  onPassed: () => void;
  /** Disables Finish while the module-complete call is in flight. */
  finishing?: boolean;
}

export function TrainingQuizPlayer({
  moduleId,
  onPassed,
  finishing = false,
}: TrainingQuizPlayerProps) {
  const {
    data: quiz,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["training", "quiz", moduleId],
    queryFn: () => getModuleQuiz(moduleId),
  });

  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [result, setResult] = useState<AttemptResult | null>(null);

  const submit = useMutation({
    mutationFn: () => submitQuizAttempt(moduleId, answers),
    onSuccess: (r) => {
      setResult(r);
      if (r.passed) triggerQuizPassed();
    },
    onError: (e) => {
      toast.error(
        e instanceof Error ? e.message : "Failed to submit the quiz",
      );
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-2 w-full rounded-full" />
        <Skeleton className="h-40 w-full rounded-md" />
      </div>
    );
  }
  if (isError || !quiz || quiz.questions.length === 0) {
    return (
      <div className="text-sm text-destructive">
        Could not load this module's quiz.
      </div>
    );
  }

  if (result) {
    const missed = quiz.questions.filter((q) => result.results[q.id] === false);
    return (
      <div className="space-y-4" data-testid="quiz-result">
        <div className="space-y-1 text-center">
          <div className="text-3xl font-semibold">{result.score}%</div>
          <p className="text-sm text-muted-foreground">
            {result.correct_count} of {result.question_count} correct — you
            need {result.pass_threshold}% to pass.
          </p>
          <p
            className={
              result.passed
                ? "font-medium text-green-600 dark:text-green-500"
                : "font-medium text-destructive"
            }
          >
            {result.passed ? "You passed!" : "Not quite — give it another go."}
          </p>
        </div>

        <ul className="space-y-2">
          {quiz.questions.map((q) => (
            <li key={q.id} className="flex items-start gap-2 text-sm">
              {result.results[q.id] ? (
                <CheckCircle2Icon className="mt-0.5 size-4 shrink-0 text-green-600 dark:text-green-500" />
              ) : (
                <XCircleIcon className="mt-0.5 size-4 shrink-0 text-destructive" />
              )}
              <span
                className={
                  result.results[q.id] ? "" : "text-muted-foreground"
                }
              >
                {q.prompt}
              </span>
            </li>
          ))}
        </ul>
        {missed.length > 0 && !result.passed && (
          <p className="text-xs text-muted-foreground">
            Review the marked questions in the training, then retake the quiz.
          </p>
        )}

        <div className="flex justify-end">
          {result.passed ? (
            <Button onClick={onPassed} disabled={finishing} aria-busy={finishing}>
              {finishing ? "Finishing…" : "Finish"}
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => {
                setResult(null);
                setAnswers({});
                setIndex(0);
              }}
            >
              Retake quiz
            </Button>
          )}
        </div>
      </div>
    );
  }

  const question = quiz.questions[index];
  const selected = answers[question.id];
  const isLast = index === quiz.questions.length - 1;
  const allAnswered = quiz.questions.every((q) => answers[q.id] !== undefined);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold">{quiz.title}</h2>
          <span className="shrink-0 text-xs text-muted-foreground">
            Question {index + 1} of {quiz.questions.length}
          </span>
        </div>
        <Progress value={((index + 1) / quiz.questions.length) * 100} />
      </div>

      <p className="text-sm font-medium">{question.prompt}</p>

      <RadioGroup
        key={question.id}
        value={selected !== undefined ? String(selected) : undefined}
        onValueChange={(v) =>
          setAnswers((prev) => ({ ...prev, [question.id]: Number(v) }))
        }
        className="gap-2"
      >
        {question.options.map((option, i) => (
          <Label
            key={i}
            className="flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm font-normal has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
          >
            <RadioGroupItem value={String(i)} />
            {option}
          </Label>
        ))}
      </RadioGroup>

      <div className="flex justify-between">
        <Button
          variant="ghost"
          onClick={() => setIndex((i) => i - 1)}
          disabled={index === 0}
        >
          Back
        </Button>
        {isLast ? (
          <Button
            onClick={() => submit.mutate()}
            disabled={!allAnswered || submit.isPending}
            aria-busy={submit.isPending}
          >
            {submit.isPending ? "Submitting…" : "Submit quiz"}
          </Button>
        ) : (
          <Button
            onClick={() => setIndex((i) => i + 1)}
            disabled={selected === undefined}
          >
            Next
          </Button>
        )}
      </div>
    </div>
  );
}
